import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { App as CapacitorApp } from '@capacitor/app';
import { nativeLinkTarget } from '../../appLinks/links';
import { useOnboarding } from '../../onboarding/OnboardingProvider';

/**
 * Universal Links и явная кнопка «Открыть в WIW» ведут в тот же SPA-маршрут
 * при холодном и повторном запуске. OAuth сохраняет query/hash исключительно
 * для HTTPS callback нашего домена; внешние адреса и произвольные схемы
 * отбрасываются общим правилом nativeLinkTarget.
 */
export default function AppUrlListener() {
  const navigate = useNavigate();
  const { dismiss, launchResolved } = useOnboarding();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    let active = true;
    let receivedEvent = false;
    let launchTarget = null;

    const open = (data, fromLaunch = false) => {
      if (!active || typeof data?.url !== 'string') return;
      const target = nativeLinkTarget(data.url);
      if (!target) return;
      dismiss();
      if (fromLaunch) {
        // A live event is newer than the launch snapshot, even if its URL differs.
        if (receivedEvent) return;
        launchTarget = target;
      } else {
        receivedEvent = true;
        const duplicate = target === launchTarget;
        launchTarget = null;
        if (duplicate) return;
      }
      navigateRef.current(target);
    };

    // Subscribe first: native may deliver the same link through both channels.
    const listenerPromise = CapacitorApp.addListener('appUrlOpen', data => open(data))
      .catch(() => { console.error('Could not register native link listener'); });
    const isNative = window.Capacitor?.isNativePlatform?.() === true
      || window.location.protocol === 'capacitor:';
    // A stuck bridge must not hold session/content initialization indefinitely.
    // Unknown launch provenance skips teaching, but a late valid link still opens.
    const launchTimer = isNative ? setTimeout(() => { if (active) launchResolved(true); }, 2000) : null;
    if (isNative && typeof CapacitorApp.getLaunchUrl === 'function') {
      CapacitorApp.getLaunchUrl().then(data => open(data, true))
        .then(() => { clearTimeout(launchTimer); if (active) launchResolved(); })
        .catch(() => { clearTimeout(launchTimer); if (active) { launchResolved(true); console.error('Could not read native launch link'); } });
    } else { clearTimeout(launchTimer); launchResolved(isNative); }

    return () => {
      active = false;
      clearTimeout(launchTimer);
      listenerPromise.then(listener => listener?.remove())
        .catch(() => { console.error('Could not remove native link listener'); });
    };
  }, [dismiss, launchResolved]);

  return null;
}
